import { omit, onlyDefined } from '@acme/common'
import { zod } from '@acme/core'
import { z } from 'zod'
import ApiService from '../api.service'

const numberNullish = z.coerce.number().nonnegative().nullish()
const stringNullish = z.coerce.string().nullish()
const porcentagemStringNullish = z.coerce
    .string()
    .trim()
    .max(5, 'Máximo 5 caracteres')
    .nullish()
    .refine(it => {
        if (!it) return true
        const regex = /^[A-Za-z0-9.-]+$/
        return regex.test(it)
    }, 'Formato inválido, utilize apenas letras, números, ponto ou hífen')
const booleanNullish = z.coerce
    .boolean()
    .nullish()
    .transform(it => {
        return !it ? 'N' : 'S'
    })
const timeFormat = (nullable: boolean) =>
    (nullable
        ? stringNullish
        : z.coerce.string().nonempty('Formato inválido')
    ).transform(it => {
        if (!it && nullable) return null
        const regex = new RegExp('^[0-9]{2}:[0-9]{2}$')
        if (!regex.test(it || '')) throw new Error('Formato inválido')
        return it
    })

const horasExtrasSchema = z
    .object({
        todas: z.boolean().default(false),
        porcentagemDemaisHrs: porcentagemStringNullish,
        porcentagemPrimeirasHrs: porcentagemStringNullish,
        qtdPrimeirasHrs: stringNullish,
        prioridadeCompensacao: z.enum(['maior', 'menor']).nullish(),
        controleTurno: booleanNullish,
        maximoHorasDiarias: timeFormat(true),
    })
    .nullish()

const regrasCalculoSchema = zod.RegrasCalculoSchema.pick({
    id: true,
    clienteId: true,
}).extend({
    nome: z.coerce.string().nonempty('Nome é obrigatório'),
    horasExtras: z.record(horasExtrasSchema),
    adicionalNoturno: numberNullish,
    compensarJustificadas: booleanNullish,
    compensarInjustificadas: booleanNullish,
    compensacaoDeHoras: booleanNullish,
    horasExtrasFeriado: booleanNullish,
    hrsExtrasNoturnas: booleanNullish,
    compensarHrsExtrasNoturnas: booleanNullish,
    tipoHorasExcedentes: z
        .enum(['DIARIO', 'SEMANAL', 'MENSAL'])
        .default('DIARIO'),
    toleranciaCLT: booleanNullish,
    controlarBancoHoras: booleanNullish,
    maximoHorasDiarias: timeFormat(true),
    horasInterjornada: timeFormat(false),
    compensacaoManual: booleanNullish,
    calcularHorasSuprimidas: booleanNullish,
    faltaAutomatica: z.enum(['injustificada', 'justificada']).nullish(),
    horasFaltaAutomatica: timeFormat(true),
    porcentagens: z
        .array(
            z.object({
                id: numberNullish,
                porcentagem: porcentagemStringNullish,
                compensacao: z.number(),
                persiste: z.boolean().default(true),
            }),
        )
        .transform(values => values.filter(onlyDefined))
        .refine(
            data => {
                const porcentagens = data.map(item => item.porcentagem)
                return new Set(porcentagens).size === porcentagens.length
            },
            {
                message: 'Não pode ter duas ou mais porcentagens iguais',
            },
        ),
    horasExcedentesConsideraDiaInteiro: booleanNullish,
})

function regraTransform<T extends Partial<z.infer<typeof regrasCalculoSchema>>>(
    it: T,
) {
    it.horasExtras ??= {}
    switch (it.tipoHorasExcedentes) {
        case 'DIARIO':
            return {
                ...it,
                horasExtras: omit(it.horasExtras, ['mensal', 'semanal']),
            }
        case 'SEMANAL':
            return {
                ...it,
                horasExtras: {
                    semanal: it.horasExtras.semanal,
                },
            }
        case 'MENSAL':
            return {
                ...it,
                horasExtras: {
                    mensal: it.horasExtras.mensal,
                },
            }
        default:
            return it
    }
}

function refineLength<T extends Partial<z.infer<typeof regrasCalculoSchema>>>(
    it: T,
) {
    it.horasExtras ??= {}
    return (
        Array.from(
            new Set(
                Object.values(it.horasExtras)
                    .flatMap(it => [
                        it?.porcentagemPrimeirasHrs,
                        it?.porcentagemDemaisHrs,
                    ])
                    .filter(Boolean),
            ),
        ).length > 0
    )
}

export const createRegrasCalculoSchema = regrasCalculoSchema
    .omit({
        id: true,
        clienteId: true,
    })
    .transform(regraTransform)
    .refine(refineLength, {
        message: 'Defina as porcentagens para as regras',
        path: ['horasExtras'],
    })

export const updateRegrasCalculoSchema = regrasCalculoSchema
    .required({
        id: true,
        clienteId: true,
    })
    .transform(regraTransform)
    .refine(refineLength, {
        message: 'Defina as porcentagens para as regras',
        path: ['horasExtras'],
    })

export type RegraCalculoPresenter = Omit<
    z.infer<typeof regrasCalculoSchema>,
    'porcentagens'
> & {
    Porcentagens: PorcentagemRegraCalculo[]
    porcentagens: PorcentagemRegraCalculo[]
}

export type PorcentagemRegraCalculo = z.infer<
    typeof regrasCalculoSchema.shape.porcentagens
>[0]

export const regrasCalculo = new ApiService<RegraCalculoPresenter>(
    'regras-calculo',
)
